import { Choice, SkipNav } from "@cmsgov/ds-medicare-gov";
import React, { FunctionComponent } from "react";
import { formatStringPhone, useTranslate } from "../../../helpers";
import { toTitleCase } from "../../../helpers/formatHelpers";
import { normalizeStringCase } from "../helpers";
import { ToggledFrom } from "../types";
import {
  baseAddPharmacyClassName,
  labelIdSuffix,
  nameIdSuffix,
} from "./constants";
import { InNetworkLabel, OutOfNetworkLabel } from "../../../components";
import classNames from "classnames";

export interface PharmacyCardProps {
  address: string;
  index: number;
  isSelected: boolean;
  inNetwork: boolean;
  handleToggle: (
    toggledFrom?: ToggledFrom
  ) => (npi: string | number, pharmacyNumber: number) => void;
  name: string;
  npi: string;
  pharmacyAnchorTarget: string;
  pharmacyDrugCosts?: JSX.Element;
  pharmacyNumber: number;
  phone: string;
  preferred: boolean;
  shouldShowSkipNav: boolean;
  shouldShowNetworkStatus: boolean;
}

export const pharmacyCardTestId = "pharmacy-card";

export const PharmacyCard: FunctionComponent<PharmacyCardProps> = ({
  address,
  index,
  inNetwork,
  isSelected,
  handleToggle,
  name,
  npi,
  pharmacyAnchorTarget,
  pharmacyDrugCosts,
  pharmacyNumber,
  phone,
  preferred,
  shouldShowSkipNav,
  shouldShowNetworkStatus,
}) => {
  const t = useTranslate();
  const pharmacyNameId = `${npi}${nameIdSuffix}`;
  const pharmacyLabelId = `${npi}${labelIdSuffix}`;
  const pharmacyNetworkLabel = inNetwork ? (
    <InNetworkLabel preferred={preferred} />
  ) : (
    <OutOfNetworkLabel />
  );

  const shouldShowDrugCosts = !!pharmacyDrugCosts;
  return (
    <li
      className="SelectPharmaciesPage__pharmacyCard"
      data-testid={pharmacyCardTestId}
      key={npi}
      id={npi}
      role="listitem"
    >
      <div className="ds-u-display--flex">
        <span className="SelectPharmaciesPage__pharmacyNumber">
          {pharmacyNumber}.
        </span>
        <div className="SelectPharmaciesPage__pharmacyInfo">
          <h2
            id={pharmacyNameId}
            className={`SelectPharmaciesPage__pharmacyCardHeader ${shouldShowNetworkStatus &&
              "ds-u-margin-bottom--0"} `}
          >
            {toTitleCase(normalizeStringCase(name))}
          </h2>
          <p className="ds-u-margin--0 SelectPharmaciesPage__pharmacyCard__status-container">
            {shouldShowNetworkStatus && pharmacyNetworkLabel}
          </p>
          <p className="ds-u-margin--0">
            {address}
            <br />
            {formatStringPhone(phone)}
          </p>
        </div>
      </div>

      <div
        className={classNames(baseAddPharmacyClassName, {
          [`${baseAddPharmacyClassName}--with-costs`]: shouldShowDrugCosts,
          [`${baseAddPharmacyClassName}--without-costs`]: !shouldShowDrugCosts,
        })}
      >
        {shouldShowDrugCosts && (
          <div className="SelectPharmaciesPage__pharmacyDrugCosts">
            {pharmacyDrugCosts}
          </div>
        )}
        <Choice
          type="checkbox"
          name="pharmacy-1"
          value={npi}
          className={`SelectPharmaciesPage__pharmaciesOption e2e-select-pharmacy-${index}`}
          checked={isSelected}
          onChange={(): void => handleToggle()(npi, pharmacyNumber)}
          aria-labelledby={`${pharmacyNameId} ${pharmacyLabelId}`}
        >
          <span id={pharmacyLabelId}>
            {/* Add sr-only comma and space for screen-reader intelligibility */}
            <span className="ds-u-visibility--screen-reader">{`, `}</span>
            {isSelected
              ? t("pharm_selection.pharmacy_added")
              : t("pharm_selection.add_pharmacy")}
          </span>
        </Choice>
      </div>

      {shouldShowSkipNav && (
        <SkipNav
          href={`#${pharmacyAnchorTarget}`}
          onClick={(e): void => {
            e.preventDefault();
            document.getElementById(pharmacyAnchorTarget)?.focus();
          }}
        >
          {t("pharmacy_skip_text")}
        </SkipNav>
      )}
    </li>
  );
};
